import {
  HELP,
  DASHBOARD,
  LM_DASHBOARD,
  HR_DASHBOARD,
  TEAM_ATTENDANCE,
} from '@constants/internal-route.constant'
import {useAppMenu} from '@context/AppMenu'
import {useMyContext} from '@context/MyContext'
import {IMenuGroup, IMenuItem} from '@services/api/menu.service'
import {usePermissions} from '@services/helper/permission.helper'
import {useLang} from '_metronic/i18n/Metronici18n'
import {SidebarMenuItem} from './SidebarMenuItem'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'

/** Used when a menu row has no icon set. */
const DEFAULT_ICON = 'bi-dot'

/** The one group shown open until the viewer folds it (Employee Self Service). */
const OPEN_BY_DEFAULT = 'HRM_G_ESS'

/**
 * Visibility rules that a role grant cannot express.
 *
 * "My Team Attn" lists the viewer's direct reports, so it is meaningful only to a line manager —
 * but line manager is not a role. It is derived at runtime from subordinateCount (@context/MyContext),
 * so it cannot live in auth.role_features alongside the grant. Keyed by url, the identifier the menu
 * and the ACL already share. If a second item ever needs a rule like this, promote it to a column
 * on auth.features rather than growing this map.
 */
const RUNTIME_VISIBLE: Record<string, (ctx: {isLineManager: boolean}) => boolean> = {
  [TEAM_ATTENDANCE]: (ctx) => ctx.isLineManager,
}

/** Whether a granted menu url also passes its runtime rule. Shared with pages/landing/Landing. */
export const isRuntimeVisible = (url: string, ctx: {isLineManager: boolean}) => {
  const rule = RUNTIME_VISIBLE[url]
  return rule ? rule(ctx) : true
}

/**
 * The whole hrm navigation: the dashboards, then every menu group from sso `auth.features`
 * (GET /menu/ERPBOI_HRM via @context/AppMenu), then Help.
 *
 * The groups, their pages, labels (title / title_bn), icons and order all come from the database and
 * are edited in the SSO menu editor — nothing here is a hardcoded menu. Role filtering and empty-group
 * removal happen server-side (MenuService.getAppMenu), so items are NOT re-gated here: `gated` would
 * re-check the same ACL against a second source of truth, and the two could disagree.
 */
export const SidebarMenuMain = () => {
  const lang = useLang()
  const {menu} = useAppMenu()
  const {isLineManager} = useMyContext()
  const {hasFeature} = usePermissions()

  /** Bangla where a translation exists, English otherwise. */
  const label = (n: IMenuItem | IMenuGroup) => (lang === 'bn' && n.titleBn ? n.titleBn : n.title)

  const isVisible = (item: IMenuItem) => isRuntimeVisible(item.url, {isLineManager})

  return (
    <>
      {/* Each dashboard is a grant (HRM_DASH_MY / _TEAM / _HR on Role Permission); Team also needs a
          direct report, like My Team Attn. */}
      {hasFeature(DASHBOARD) && (
        <SidebarMenuItem to={DASHBOARD} title='My Dashboard' fontIcon='bi-columns-gap' />
      )}
      {isLineManager && hasFeature(LM_DASHBOARD) && (
        <SidebarMenuItem to={LM_DASHBOARD} title='Team Dashboard' fontIcon='bi-people' />
      )}
      {hasFeature(HR_DASHBOARD) && (
        <SidebarMenuItem to={HR_DASHBOARD} title='HR Dashboard' fontIcon='bi-building' />
      )}

      {menu.map((group: IMenuGroup) => {
        // The server already dropped groups with no granted children; re-check after the runtime
        // filter so a group emptied here does not render as an empty accordion.
        const items = group.children.filter(isVisible)
        if (!items.length) return null

        return (
          <SidebarMenuItemWithSub
            key={group.featureId}
            id={group.code || group.featureId}
            title={label(group)}
            fontIcon={group.icon || DEFAULT_ICON}
            childUrls={items.map((item) => item.url)}
            defaultOpen={group.code === OPEN_BY_DEFAULT}
          >
            {items.map((item) => (
              <SidebarMenuItem
                key={item.featureId}
                to={item.url}
                title={label(item)}
                fontIcon={item.icon || DEFAULT_ICON}
              />
            ))}
          </SidebarMenuItemWithSub>
        )
      })}

      <SidebarMenuItem to={HELP} title='Help' fontIcon='bi-question-circle' />
    </>
  )
}
